import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

// ─── Style constants ───────────────────────────────────────────────────────

const COLORS = {
	headerBg: "FF1D54A2",
	headerText: "FFFFFFFF",
	titleText: "FF1D54A2",
	totalBg: "FFF5F7FA",
	totalText: "FF1D54A2",
	border: "FFD9E2EC",
	altRow: "FFF9FAFB",
};

// Selectable export sections. "override" spans two worksheets.
export const EXPORT_SHEETS = [
	{ key: "override", label: "Override Report" },
	{ key: "performance", label: "Agent Performance" },
	{ key: "leadership", label: "Leadership Bonuses" },
	{ key: "totals", label: "Total Computations" },
];

export const EXPORT_SHEET_KEYS = EXPORT_SHEETS.map((s) => s.key);

const CURRENCY_FORMAT = "$#,##0.00";
const INTEGER_FORMAT = "#,##0";
const DATE_FORMAT = "mmm dd, yyyy";

const THIN_BORDER = {
	top: { style: "thin", color: { argb: COLORS.border } },
	left: { style: "thin", color: { argb: COLORS.border } },
	bottom: { style: "thin", color: { argb: COLORS.border } },
	right: { style: "thin", color: { argb: COLORS.border } },
};

// ─── Style helpers ─────────────────────────────────────────────────────────

const styleTitleCell = (cell) => {
	cell.font = { size: 18, bold: true, color: { argb: COLORS.titleText } };
};

const styleMetaLabelCell = (cell) => {
	cell.font = { bold: true };
};

const styleHeaderRow = (row) => {
	row.eachCell((cell) => {
		cell.font = { bold: true, color: { argb: COLORS.headerText } };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.headerBg } };
		cell.alignment = { vertical: "middle", horizontal: "center" };
		cell.border = THIN_BORDER;
	});
	row.height = 20;
};

const styleDataRow = (row, { shaded = false } = {}) => {
	row.eachCell((cell) => {
		cell.border = THIN_BORDER;
		if (shaded) {
			cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.altRow } };
		}
	});
};

const styleTotalRow = (row) => {
	row.eachCell((cell) => {
		cell.font = { bold: true, color: { argb: COLORS.totalText } };
		cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COLORS.totalBg } };
		cell.border = { ...THIN_BORDER, top: { style: "thin", color: { argb: COLORS.totalText } } };
	});
};

const autoSizeColumns = (worksheet, { min = 10, max = 45 } = {}) => {
	worksheet.columns.forEach((column) => {
		let longest = min;
		column.eachCell({ includeEmpty: true }, (cell) => {
			const length = cell.value != null ? String(cell.value).length : 0;
			if (length > longest) longest = length;
		});
		column.width = Math.min(longest + 2, max);
	});
};

/**
 * Writes the title row + Period/Generated metadata block that sits above
 * every table. Returns the row number the column header row should start on.
 */
const addTitleAndMeta = (worksheet, { title, periodLabel, generatedLabel, columnCount }) => {
	worksheet.mergeCells(1, 1, 1, columnCount);
	const titleCell = worksheet.getCell(1, 1);
	titleCell.value = title;
	styleTitleCell(titleCell);
	worksheet.getRow(1).height = 26;

	const periodLabelCell = worksheet.getCell(2, 1);
	periodLabelCell.value = "Period";
	styleMetaLabelCell(periodLabelCell);
	worksheet.getCell(2, 2).value = periodLabel;

	const generatedLabelCell = worksheet.getCell(3, 1);
	generatedLabelCell.value = "Generated";
	styleMetaLabelCell(generatedLabelCell);
	worksheet.getCell(3, 2).value = generatedLabel;

	// Row 4 is left blank on purpose (spacer before the table).
	const headerRowNumber = 5;
	worksheet.views = [{ state: "frozen", ySplit: headerRowNumber }];
	return headerRowNumber;
};

// ─── Worksheet 1 — Override Summary ───────────────────────────────────────

const buildOverrideSummarySheet = (workbook, { overrideAgents, periodLabel, generatedLabel }) => {
	const worksheet = workbook.addWorksheet("Override Summary");

	const columns = [
		{ header: "Rank", key: "rank", width: 8 },
		{ header: "Agent Name", key: "name", width: 26 },
		{ header: "Total Override Earnings", key: "total", width: 22 },
		{ header: "Payment Status", key: "paymentStatus", width: 18 },
		{ header: "Override Status", key: "overrideStatus", width: 18 },
	];

	const headerRowNumber = addTitleAndMeta(worksheet, {
		title: "Override Report",
		periodLabel,
		generatedLabel,
		columnCount: columns.length,
	});

	worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

	const headerRow = worksheet.getRow(headerRowNumber);
	columns.forEach((col, i) => (headerRow.getCell(i + 1).value = col.header));
	styleHeaderRow(headerRow);

	let rowNumber = headerRowNumber + 1;
	let totalOverride = 0;

	overrideAgents.forEach((agent, index) => {
		const row = worksheet.getRow(rowNumber);
		row.getCell(1).value = index + 1;
		row.getCell(2).value = agent.name;
		row.getCell(3).value = agent.totalOverride;
		row.getCell(3).numFmt = CURRENCY_FORMAT;
		row.getCell(4).value = agent.paymentStatus;
		row.getCell(4).alignment = { horizontal: "center" };
		row.getCell(5).value = agent.qualified ? "Eligible" : "Not Eligible";
		row.getCell(5).alignment = { horizontal: "center" };
		styleDataRow(row, { shaded: index % 2 === 1 });
		totalOverride += agent.totalOverride;
		rowNumber += 1;
	});

	const totalRow = worksheet.getRow(rowNumber);
	totalRow.getCell(1).value = "";
	totalRow.getCell(2).value = "Total";
	totalRow.getCell(3).value = totalOverride;
	totalRow.getCell(3).numFmt = CURRENCY_FORMAT;
	styleTotalRow(totalRow);

	autoSizeColumns(worksheet);
	return worksheet;
};

// ─── Worksheet 2 — Override Details (flattened downline) ─────────────────

const buildOverrideDetailsSheet = (workbook, { overrideAgents, periodLabel, generatedLabel }) => {
	const worksheet = workbook.addWorksheet("Override Details");

	const columns = [
		{ header: "Upline Agent", key: "upline", width: 24 },
		{ header: "Total Override", key: "uplineTotal", width: 16 },
		{ header: "Payment Status", key: "paymentStatus", width: 16 },
		{ header: "Override Status", key: "overrideStatus", width: 16 },
		{ header: "Level", key: "level", width: 8 },
		{ header: "Downline Agent", key: "downline", width: 24 },
		{ header: "Active Members", key: "members", width: 16 },
		{ header: "Rate", key: "rate", width: 10 },
		{ header: "Override Earned", key: "earned", width: 16 },
	];

	const headerRowNumber = addTitleAndMeta(worksheet, {
		title: "Override Downline Details",
		periodLabel,
		generatedLabel,
		columnCount: columns.length,
	});

	worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

	const headerRow = worksheet.getRow(headerRowNumber);
	columns.forEach((col, i) => (headerRow.getCell(i + 1).value = col.header));
	styleHeaderRow(headerRow);

	let rowNumber = headerRowNumber + 1;
	let shadeToggle = false;

	overrideAgents.forEach((agent) => {
		["L1", "L2", "L3"].forEach((level) => {
			const downlineAgents = agent.downline?.[level] || [];
			downlineAgents.forEach((member) => {
				const row = worksheet.getRow(rowNumber);
				row.getCell(1).value = agent.name;
				row.getCell(2).value = agent.totalOverride;
				row.getCell(2).numFmt = CURRENCY_FORMAT;
				row.getCell(3).value = agent.paymentStatus;
				row.getCell(3).alignment = { horizontal: "center" };
				row.getCell(4).value = agent.qualified ? "Eligible" : "Not Eligible";
				row.getCell(4).alignment = { horizontal: "center" };
				row.getCell(5).value = level;
				row.getCell(5).alignment = { horizontal: "center" };
				row.getCell(6).value = member.name;
				row.getCell(7).value = member.members;
				row.getCell(7).numFmt = INTEGER_FORMAT;
				row.getCell(8).value = member.rate;
				row.getCell(8).numFmt = CURRENCY_FORMAT;
				row.getCell(9).value = member.earned;
				row.getCell(9).numFmt = CURRENCY_FORMAT;
				styleDataRow(row, { shaded: shadeToggle });
				shadeToggle = !shadeToggle;
				rowNumber += 1;
			});
		});
	});

	autoSizeColumns(worksheet);
	return worksheet;
};

// ─── Worksheet 3 — Agent Performance ──────────────────────────────────────

const buildAgentPerformanceSheet = (workbook, { performanceRows, periodLabel, generatedLabel }) => {
	const worksheet = workbook.addWorksheet("Agent Performance");

	const columns = [
		{ header: "Rank", key: "rank", width: 8 },
		{ header: "Agent Name", key: "name", width: 26 },
		{ header: "Personal Active Members", key: "personal", width: 22 },
		{ header: "Network Members", key: "network", width: 18 },
		{ header: "Policy Count", key: "policyCount", width: 16 },
		{ header: "Licensing", key: "licensing", width: 14 },
	];

	const headerRowNumber = addTitleAndMeta(worksheet, {
		title: "Agent Performance",
		periodLabel,
		generatedLabel,
		columnCount: columns.length,
	});

	worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

	const headerRow = worksheet.getRow(headerRowNumber);
	columns.forEach((col, i) => (headerRow.getCell(i + 1).value = col.header));
	styleHeaderRow(headerRow);

	let rowNumber = headerRowNumber + 1;
	let totalPolicyCount = 0;

	performanceRows.forEach((agent, index) => {
		const row = worksheet.getRow(rowNumber);
		row.getCell(1).value = index + 1;
		row.getCell(2).value = agent.name;
		row.getCell(3).value = agent.personal;
		row.getCell(3).numFmt = INTEGER_FORMAT;
		row.getCell(4).value = agent.network;
		row.getCell(4).numFmt = INTEGER_FORMAT;
		row.getCell(5).value = agent.policyCount;
		row.getCell(5).numFmt = INTEGER_FORMAT;
		row.getCell(6).value = agent.licensing;
		row.getCell(6).alignment = { horizontal: "center" };
		styleDataRow(row, { shaded: index % 2 === 1 });
		totalPolicyCount += agent.policyCount;
		rowNumber += 1;
	});

	const totalRow = worksheet.getRow(rowNumber);
	totalRow.getCell(1).value = "";
	totalRow.getCell(2).value = "Total";
	totalRow.getCell(5).value = totalPolicyCount;
	totalRow.getCell(5).numFmt = INTEGER_FORMAT;
	styleTotalRow(totalRow);

	autoSizeColumns(worksheet);
	return worksheet;
};

// ─── Worksheet 4 — Leadership Bonuses ─────────────────────────────────────

const buildLeadershipBonusesSheet = (workbook, { leadershipRows, periodLabel, generatedLabel }) => {
	const worksheet = workbook.addWorksheet("Leadership Bonuses");

	const columns = [
		{ header: "Rank", key: "rank", width: 8 },
		{ header: "Agent", key: "agent", width: 24 },
		{ header: "Milestone", key: "milestone", width: 20 },
		{ header: "Current Members", key: "current", width: 18 },
		{ header: "Target", key: "target", width: 12 },
		{ header: "Bonus", key: "bonus", width: 14 },
		{ header: "Achieved Date", key: "achievedDate", width: 16 },
		{ header: "Status", key: "status", width: 14 },
	];

	const headerRowNumber = addTitleAndMeta(worksheet, {
		title: "Leadership Bonuses",
		periodLabel,
		generatedLabel,
		columnCount: columns.length,
	});

	worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

	const headerRow = worksheet.getRow(headerRowNumber);
	columns.forEach((col, i) => (headerRow.getCell(i + 1).value = col.header));
	styleHeaderRow(headerRow);

	let rowNumber = headerRowNumber + 1;
	let totalEligibleBonuses = 0;
	let eligibleCount = 0;

	leadershipRows.forEach((row, index) => {
		const excelRow = worksheet.getRow(rowNumber);
		const bonusValue = Number(String(row.bonus).replace(/[^0-9.-]/g, "")) || 0;

		excelRow.getCell(1).value = index + 1;
		excelRow.getCell(2).value = row.agent;
		excelRow.getCell(3).value = row.milestone;
		excelRow.getCell(4).value = row.current;
		excelRow.getCell(4).numFmt = INTEGER_FORMAT;
		excelRow.getCell(5).value = row.target;
		excelRow.getCell(5).numFmt = INTEGER_FORMAT;
		excelRow.getCell(6).value = bonusValue;
		excelRow.getCell(6).numFmt = CURRENCY_FORMAT;
		if (row.achievedDate) {
			excelRow.getCell(7).value = dayjs(row.achievedDate, ["MMM D, YYYY", "YYYY-MM-DD"]).toDate();
			excelRow.getCell(7).numFmt = DATE_FORMAT;
		} else {
			excelRow.getCell(7).value = "—";
		}
		excelRow.getCell(7).alignment = { horizontal: "center" };
		excelRow.getCell(8).value = row.status;
		excelRow.getCell(8).alignment = { horizontal: "center" };

		styleDataRow(excelRow, { shaded: index % 2 === 1 });

		if (row.status === "Eligible") {
			totalEligibleBonuses += bonusValue;
			eligibleCount += 1;
		}
		rowNumber += 1;
	});

	const totalRow = worksheet.getRow(rowNumber);
	totalRow.getCell(1).value = "";
	totalRow.getCell(2).value = "Total Eligible Bonuses";
	totalRow.getCell(6).value = totalEligibleBonuses;
	totalRow.getCell(6).numFmt = CURRENCY_FORMAT;
	totalRow.getCell(8).value = `${eligibleCount} eligible`;
	totalRow.getCell(8).alignment = { horizontal: "center" };
	styleTotalRow(totalRow);

	autoSizeColumns(worksheet);
	return worksheet;
};

// ─── Worksheet 5 — Total Computations ─────────────────────────────────────

const buildMonthlyTotalsSheet = (workbook, { monthlyTotalsRows, periodLabel, generatedLabel }) => {
	const worksheet = workbook.addWorksheet("Total Computations");

	const columns = [
		{ header: "#", key: "rank", width: 8 },
		{ header: "Agent Name", key: "name", width: 26 },
		{ header: "Override Earnings", key: "overrideEarnings", width: 18 },
		{ header: "Network Residual Income", key: "residualIncome", width: 22 },
		{ header: "Personal Book Service Fees", key: "serviceFees", width: 24 },
		{ header: "Net Monthly", key: "netMonthly", width: 16 },
		{ header: "Direction", key: "netDirection", width: 14 },
		// No opt-in column: agents opted out for this period are excluded from the
		// report the sheet is built from, so every row here is an opted-in agent.
	];

	const headerRowNumber = addTitleAndMeta(worksheet, {
		title: "Total Computations",
		periodLabel,
		generatedLabel,
		columnCount: columns.length,
	});

	worksheet.columns = columns.map((c) => ({ key: c.key, width: c.width }));

	const headerRow = worksheet.getRow(headerRowNumber);
	columns.forEach((col, i) => (headerRow.getCell(i + 1).value = col.header));
	styleHeaderRow(headerRow);

	let rowNumber = headerRowNumber + 1;
	let totalOverride = 0;
	let totalResidual = 0;
	let totalFees = 0;
	let totalPayable = 0;
	let totalPayout = 0;

	monthlyTotalsRows.forEach((agent, index) => {
		const row = worksheet.getRow(rowNumber);
		row.getCell(1).value = index + 1;
		row.getCell(2).value = agent.name;
		row.getCell(3).value = agent.overrideEarnings;
		row.getCell(3).numFmt = CURRENCY_FORMAT;
		row.getCell(4).value = agent.residualIncome;
		row.getCell(4).numFmt = CURRENCY_FORMAT;
		row.getCell(5).value = agent.serviceFees;
		row.getCell(5).numFmt = CURRENCY_FORMAT;
		// Magnitude here; the Direction column carries the sign.
		row.getCell(6).value = Math.abs(agent.netMonthly);
		row.getCell(6).numFmt = CURRENCY_FORMAT;
		row.getCell(7).value =
			agent.netDirection === "payout" ? "Payout to Agent" : agent.netDirection === "payable" ? "Agents Need to Pay" : "Settled";
		row.getCell(7).alignment = { horizontal: "center" };
		styleDataRow(row, { shaded: index % 2 === 1 });

		totalOverride += agent.overrideEarnings;
		totalResidual += agent.residualIncome;
		totalFees += agent.serviceFees;
		totalPayable += agent.payable;
		totalPayout += agent.payout;
		rowNumber += 1;
	});

	const totalRow = worksheet.getRow(rowNumber);
	totalRow.getCell(1).value = "";
	totalRow.getCell(2).value = "Total";
	totalRow.getCell(3).value = totalOverride;
	totalRow.getCell(3).numFmt = CURRENCY_FORMAT;
	totalRow.getCell(4).value = totalResidual;
	totalRow.getCell(4).numFmt = CURRENCY_FORMAT;
	totalRow.getCell(5).value = totalFees;
	totalRow.getCell(5).numFmt = CURRENCY_FORMAT;
	styleTotalRow(totalRow);

	// Payable and payout net against each other, so they get their own summary
	// lines rather than a single misleading figure.
	rowNumber += 1;
	const payableRow = worksheet.getRow(rowNumber);
	payableRow.getCell(2).value = "Total — Agents Need to Pay";
	payableRow.getCell(6).value = totalPayable;
	payableRow.getCell(6).numFmt = CURRENCY_FORMAT;
	styleTotalRow(payableRow);

	rowNumber += 1;
	const payoutRow = worksheet.getRow(rowNumber);
	payoutRow.getCell(2).value = "Total — Payout to Agents";
	payoutRow.getCell(6).value = totalPayout;
	payoutRow.getCell(6).numFmt = CURRENCY_FORMAT;
	styleTotalRow(payoutRow);

	autoSizeColumns(worksheet);
	return worksheet;
};

// ─── Entry point ───────────────────────────────────────────────────────────

/**
 * Generates and downloads the AHG Reports workbook.
 *
 * @param {Object} params
 * @param {string} params.period - Report period in "YYYY-MM" format (e.g. "2026-07").
 * @param {string} params.generatedAt - Raw generated-at timestamp/date from the API.
 * @param {Array} params.overrideAgents - Formatted override agents (see formatOverrideAgents in Reports.jsx).
 * @param {Array} params.performanceRows - Formatted agent performance rows.
 * @param {Array} params.leadershipRows - Formatted leadership bonus rows.
 */
export async function exportReportsExcel({
	period,
	generatedAt,
	overrideAgents = [],
	performanceRows = [],
	leadershipRows = [],
	monthlyTotalsRows = [],
	sheets = EXPORT_SHEET_KEYS,
}) {
	const periodDate = period ? dayjs(`${period}-01`) : dayjs();
	const periodLabel = periodDate.format("MMMM YYYY");
	const generatedLabel = generatedAt ? dayjs(generatedAt).format("MMMM D, YYYY") : dayjs().format("MMMM D, YYYY");

	const workbook = new ExcelJS.Workbook();
	workbook.creator = "Ally Health Group";
	workbook.created = new Date();

	// The Override Report tab spans two sheets — summary and per-downline detail.
	if (sheets.includes("override")) {
		buildOverrideSummarySheet(workbook, { overrideAgents, periodLabel, generatedLabel });
		buildOverrideDetailsSheet(workbook, { overrideAgents, periodLabel, generatedLabel });
	}
	if (sheets.includes("performance")) {
		buildAgentPerformanceSheet(workbook, { performanceRows, periodLabel, generatedLabel });
	}
	if (sheets.includes("leadership")) {
		buildLeadershipBonusesSheet(workbook, { leadershipRows, periodLabel, generatedLabel });
	}
	if (sheets.includes("totals")) {
		buildMonthlyTotalsSheet(workbook, { monthlyTotalsRows, periodLabel, generatedLabel });
	}

	// ExcelJS writes a corrupt file with no worksheets.
	if (workbook.worksheets.length === 0) {
		return;
	}

	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: "application/octet-stream" });
	const filename = `AHG_Reports_${periodDate.format("YYYY-MM")}.xlsx`;

	saveAs(blob, filename);
}

export default exportReportsExcel;
